import {
  Box,
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StickyTableHeadCell, StyledTableCell } from "../TableComponents";
import DownloadButton from "./DownloadButton";
import { colors } from "../../utils/colors";
import {
  FinancialExportItem,
  FinancialExportTableRowProps,
  FinancialExportTableProps,
  TableHeaderProps,
} from "./type";
import AutorenewSharpIcon from "@mui/icons-material/AutorenewSharp";
import { FinancialExportStyles } from "./styles";
import { financialExportTableHeaders } from "../../utils/consts";
import Loader from "../Loader";

const FinancialExportTable = ({
  data,
  loading,
  onDownloadClick,
  errorMessage = "",
  onRefresh,
  downloadingItems = new Set(),
}: FinancialExportTableProps) => {
  return (
    <TableContainer sx={FinancialExportStyles.tableContainer} component={Paper}>
      <Table size="small" stickyHeader>
        <TableHeader onRefresh={onRefresh} />
        <TableBody>
          {renderTableBody(
            data,
            loading,
            onDownloadClick,
            errorMessage,
            downloadingItems,
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const renderTableBody = (
  data: FinancialExportItem[],
  loading: boolean,
  onDownloadClick: (item: FinancialExportItem) => void | Promise<void>,
  errorMessage: string,
  downloadingItems: Set<number>,
) => {
  if (loading) {
    return renderEmptyState(loading, errorMessage);
  }

  if (data.length > 0) {
    return renderDataRows(data, onDownloadClick, downloadingItems);
  }

  return renderEmptyState(loading, errorMessage);
};

const renderDataRows = (
  data: FinancialExportItem[],
  onDownloadClick: (item: FinancialExportItem) => void | Promise<void>,
  downloadingItems: Set<number>,
) => {
  return data.map((item, index) => (
    <FinancialExportTableRow
      key={item.id + item.generated_on + item.export_data_type + index}
      item={item}
      index={index}
      onDownloadClick={onDownloadClick}
      loading={downloadingItems.has(item.id)}
    />
  ));
};

const renderEmptyState = (loading: boolean, errorMessage: string) => {
  return (
    <TableRow sx={FinancialExportStyles.emptyStateRow}>
      <StyledTableCell
        noBorderBottom
        noBorderLeft
        noBorderRight
        noBorderTop
        colSpan={9}
        align="center"
      >
        {loading ? (
          <Loader />
        ) : (
          <Typography sx={FinancialExportStyles.emptyStateText}>
            {errorMessage === "" ? "No Data Available" : errorMessage}
          </Typography>
        )}
      </StyledTableCell>
    </TableRow>
  );
};

const FinancialExportTableRow = ({
  item,
  onDownloadClick,
  loading = false,
}: FinancialExportTableRowProps) => {
  return (
    <TableRow>
      <StyledTableCell
        sx={FinancialExportStyles.nameCell}
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.space_employee}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.generated_on}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.export_data_type}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.company_names ? item.company_names : "-"}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.source ? item.source : "-"}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.from_date} - {item.to_date}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.additional_params ? item.additional_params : "-"}
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        <DownloadButton
          loading={loading}
          onClick={() => onDownloadClick?.(item)}
        />
      </StyledTableCell>
      <StyledTableCell
        fontWeight={400}
        fontSize="1.0rem"
        textAlign="left"
        color={colors.black}
        noBorderTop
        noBorderRight
        noBorderLeft
      >
        {item.file_download_status ? item.file_download_status : "-"}
      </StyledTableCell>
    </TableRow>
  );
};

const TableHeader = ({ onRefresh }: TableHeaderProps) => {
  return (
    <TableHead>
      <TableRow>
        {financialExportTableHeaders.map((header, index) => (
          <StickyTableHeadCell
            key={header.key}
            sx={FinancialExportStyles.tableHeader}
            noBorderTop
            noBorderBottom
            noBorderLeft
            noBorderRight
            textAlign="left"
            fontSize="1.0rem"
            fontWeight={500}
            minWidth={index === 0 ? "6.25 rem" : "5.0 rem"}
            fontColor={index === 0 ? "#343A40" : "#000000"}
          >
            <Box sx={FinancialExportStyles.statusCell}>
              {header.label}

              {header.key === "status" && (
                <ButtonBase onClick={onRefresh}>
                  <Box sx={FinancialExportStyles.refreshButton}>
                    <AutorenewSharpIcon
                      fontSize="medium"
                      sx={FinancialExportStyles.refreshIcon}
                    />
                  </Box>
                </ButtonBase>
              )}
            </Box>
          </StickyTableHeadCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default FinancialExportTable;
